import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { MOCK_MATERIALS } from '@/mocks/database';
import { ProcurementCategory } from '@/types/domain';
import { Package, Plus, Trash2, Copy, Edit, ShoppingCart, FileText, X } from 'lucide-react';

export interface MaterialItemState {
  id: string;
  isCatalog: boolean;
  materialCode: string;
  materialName: string;
  category: ProcurementCategory;
  quantity: number;
  unitOfMeasure: string;
  estimatedUnitPrice: number;
  requiredDate: string;
  specifications: string;
  costCenterCode: string;
  plantCode: string;
  glAccountCode?: string;
  preferredVendor?: string;
  deliveryLocation?: string;
}

interface ItemManagementSectionProps {
  items: MaterialItemState[];
  setItems: React.Dispatch<React.SetStateAction<MaterialItemState[]>>;
  defaultCategory: ProcurementCategory;
  defaultCostCenter: string;
  defaultPlant: string;
  defaultRequiredDate: string;
}

export const ItemManagementSection: React.FC<ItemManagementSectionProps> = ({
  items,
  setItems,
  defaultCategory,
  defaultCostCenter,
  defaultPlant,
  defaultRequiredDate,
}) => {
  const [editingItem, setEditingItem] = useState<MaterialItemState | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const totalRequisitionValue = items.reduce(
    (acc, i) => acc + (Number(i.quantity) || 0) * (Number(i.estimatedUnitPrice) || 0),
    0
  );

  const handleAddNewItem = () => {
    const firstMat = MOCK_MATERIALS[0] || {
      code: 'MAT-1000',
      name: 'Standard Catalog Material',
      standardPrice: 1250,
      unitOfMeasure: 'EA',
    };
    const newItem: MaterialItemState = {
      id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 5)}`,
      isCatalog: true,
      materialCode: firstMat.code,
      materialName: firstMat.name,
      category: defaultCategory,
      quantity: 1,
      unitOfMeasure: firstMat.unitOfMeasure || 'EA',
      estimatedUnitPrice: firstMat.standardPrice || 1250,
      requiredDate: defaultRequiredDate || new Date().toISOString().split('T')[0] || '',
      specifications: '',
      costCenterCode: defaultCostCenter,
      plantCode: defaultPlant,
      glAccountCode: '500100',
      deliveryLocation: 'Main Plant Warehouse',
    };
    setEditingItem(newItem);
    setIsEditorOpen(true);
  };

  const handleEditItem = (item: MaterialItemState) => {
    setEditingItem({ ...item });
    setIsEditorOpen(true);
  };

  const handleDuplicateItem = (item: MaterialItemState) => {
    const dup: MaterialItemState = {
      ...item,
      id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 5)}`,
    };
    setItems((prev) => [...prev, dup]);
  };

  const handleRemoveItem = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const handleSaveItem = () => {
    if (!editingItem) return;
    if (editingItem.quantity <= 0) {
      alert('Quantity must be greater than zero.');
      return;
    }
    if (editingItem.estimatedUnitPrice < 0) {
      alert('Estimated Unit Price cannot be negative.');
      return;
    }

    setItems((prev) => {
      const idx = prev.findIndex((i) => i.id === editingItem.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = editingItem;
        return copy;
      }
      return [...prev, editingItem];
    });

    setIsEditorOpen(false);
    setEditingItem(null);
  };

  const handleCatalogSelect = (matCode: string) => {
    const matched = MOCK_MATERIALS.find((m) => m.code === matCode);
    if (matched && editingItem) {
      setEditingItem({
        ...editingItem,
        materialCode: matched.code,
        materialName: matched.name,
        unitOfMeasure: matched.unitOfMeasure || 'EA',
        estimatedUnitPrice: matched.standardPrice || 1000,
      });
    }
  };

  return (
    <Card className="border border-border/80 shadow-xs rounded-2xl overflow-hidden font-heading">
      <CardHeader className="pb-3.5 border-b border-border/60 bg-muted/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <CardTitle className="text-base font-extrabold flex items-center gap-2.5 text-foreground">
          <div className="h-8 w-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
            <Package className="h-4 w-4" />
          </div>
          <div>
            <span>Material Line Items ({items.length})</span>
            <p className="text-xs font-semibold text-foreground-secondary mt-0.5">
              Specify quantities and materials for purchase order generation
            </p>
          </div>
        </CardTitle>
        <div className="flex items-center gap-3 self-end sm:self-auto">
          <div className="text-right">
            <span className="text-[11px] font-bold text-foreground-muted uppercase tracking-wider block">
              Estimated Total
            </span>
            <span className="font-mono font-black text-base text-foreground">
              ₹{totalRequisitionValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <Button size="sm" onClick={handleAddNewItem} className="h-9 rounded-xl text-xs font-bold cursor-pointer">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Line Item
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {items.length === 0 ? (
          <div className="p-10 text-center space-y-3">
            <div className="h-12 w-12 rounded-2xl bg-muted/60 border border-border flex items-center justify-center mx-auto text-foreground-muted">
              <ShoppingCart className="h-6 w-6" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-foreground">No Items Added</h4>
              <p className="text-xs text-foreground-secondary mt-0.5">
                Add at least one catalog or custom material item to proceed with submission.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={handleAddNewItem} className="cursor-pointer rounded-xl font-bold">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add First Item
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="bg-muted/40 text-foreground-muted border-b border-border text-[11px] font-bold uppercase tracking-wider">
                  <th className="py-3 px-4">Line #</th>
                  <th className="py-3 px-4">Material Details</th>
                  <th className="py-3 px-4 text-right">Quantity</th>
                  <th className="py-3 px-4">UOM</th>
                  <th className="py-3 px-4 text-right">Unit Price (₹)</th>
                  <th className="py-3 px-4 text-right">Line Total (₹)</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {items.map((item, idx) => {
                  const lineTotal = (item.quantity || 0) * (item.estimatedUnitPrice || 0);
                  return (
                    <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-4 font-mono font-bold text-foreground-muted">
                        {(idx + 1) * 10}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-extrabold text-foreground">{item.materialName}</span>
                          {item.isCatalog ? (
                            <Badge variant="outline" className="text-[10px] py-0 font-mono font-bold">
                              {item.materialCode}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-[10px] py-0 font-bold bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20">
                              Custom / Non-Catalog
                            </Badge>
                          )}
                        </div>
                        {item.specifications && (
                          <p className="text-[11px] text-foreground-secondary line-clamp-1 mt-0.5">
                            {item.specifications}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-extrabold text-foreground">{item.quantity}</td>
                      <td className="py-3 px-4 text-foreground-secondary font-mono font-bold">{item.unitOfMeasure}</td>
                      <td className="py-3 px-4 text-right font-mono font-semibold text-foreground-secondary">
                        ₹{item.estimatedUnitPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-black text-foreground text-xs">
                        ₹{lineTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleEditItem(item)}
                            className="p-1.5 hover:bg-muted rounded-lg text-foreground-secondary hover:text-foreground transition-colors cursor-pointer"
                            title="Edit Item"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDuplicateItem(item)}
                            className="p-1.5 hover:bg-muted rounded-lg text-foreground-secondary hover:text-foreground transition-colors cursor-pointer"
                            title="Duplicate Item"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(item.id)}
                            className="p-1.5 hover:bg-rose-500/15 rounded-lg text-rose-500 hover:text-rose-600 transition-colors cursor-pointer"
                            title="Remove Item"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Modal Item Editor with Smooth Framer Motion Backdrop & Spring Animation */}
        <AnimatePresence>
          {isEditorOpen && editingItem && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="fixed inset-0 bg-slate-950/70 backdrop-blur-md"
                onClick={() => setIsEditorOpen(false)}
              />

              <motion.div
                role="dialog"
                aria-modal="true"
                initial={{ opacity: 0, scale: 0.9, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: 16 }}
                transition={{ type: 'spring', damping: 26, stiffness: 360 }}
                className="relative z-10 bg-surface dark:bg-[#121526] border border-border/80 rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden"
              >
                <div className="p-5 border-b border-border flex items-center justify-between bg-muted/20">
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="font-extrabold text-sm text-foreground">
                        {editingItem.id ? 'Configure Line Item' : 'Add New Line Item'}
                      </h3>
                      <p className="text-[11px] font-semibold text-foreground-secondary">
                        Catalog material selection and unit pricing
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsEditorOpen(false)}
                    className="p-1.5 rounded-xl text-foreground-secondary hover:text-foreground hover:bg-muted/60 transition-colors cursor-pointer"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
                  {/* Catalog vs Non-Catalog Selector */}
                  <div className="flex items-center gap-4 p-2.5 rounded-xl bg-muted/40 border border-border text-xs font-bold">
                    <span className="text-foreground-secondary">Item Type:</span>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="catalogToggle"
                        className="accent-primary cursor-pointer"
                        checked={editingItem.isCatalog}
                        onChange={() => setEditingItem({ ...editingItem, isCatalog: true })}
                      />
                      <span>Master Catalog Material</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="catalogToggle"
                        className="accent-primary cursor-pointer"
                        checked={!editingItem.isCatalog}
                        onChange={() =>
                          setEditingItem({
                            ...editingItem,
                            isCatalog: false,
                            materialCode: 'NON-CATALOG',
                          })
                        }
                      />
                      <span>Custom / Non-Catalog</span>
                    </label>
                  </div>

                  {/* Material Lookup or Description */}
                  {editingItem.isCatalog ? (
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Select Master Material</label>
                      <select
                        className="w-full px-3.5 py-2.5 text-xs font-bold rounded-xl border border-border bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
                        value={editingItem.materialCode}
                        onChange={(e) => handleCatalogSelect(e.target.value)}
                      >
                        {MOCK_MATERIALS.map((m) => (
                          <option key={m.code} value={m.code}>
                            {m.code} - {m.name} (₹{m.standardPrice.toLocaleString('en-IN')})
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Material Name / Description</label>
                      <input
                        type="text"
                        className="w-full px-3.5 py-2 text-xs font-bold rounded-xl border border-border bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        placeholder="e.g. Custom Liquid Cooling Server Manifold"
                        value={editingItem.materialName}
                        onChange={(e) => setEditingItem({ ...editingItem, materialName: e.target.value })}
                      />
                    </div>
                  )}

                  {/* Quantity, UOM, Unit Price */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Quantity</label>
                      <input
                        type="number"
                        min="1"
                        className="w-full px-3 py-2 text-xs font-mono font-bold rounded-xl border border-border bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        value={editingItem.quantity}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, quantity: Math.max(1, parseInt(e.target.value) || 0) })
                        }
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Unit of Measure</label>
                      <select
                        className="w-full px-3 py-2 text-xs font-mono font-bold rounded-xl border border-border bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
                        value={editingItem.unitOfMeasure}
                        onChange={(e) => setEditingItem({ ...editingItem, unitOfMeasure: e.target.value })}
                      >
                        <option value="EA">EA (Each)</option>
                        <option value="LOT">LOT (Lot)</option>
                        <option value="KG">KG (Kilograms)</option>
                        <option value="SET">SET (Set)</option>
                        <option value="BOX">BOX (Box)</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-foreground">Unit Price (₹)</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="w-full px-3 py-2 text-xs font-mono font-bold rounded-xl border border-border bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        value={editingItem.estimatedUnitPrice}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            estimatedUnitPrice: Math.max(0, parseFloat(e.target.value) || 0),
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* Calculated Subtotal Banner */}
                  <div className="p-3.5 rounded-2xl bg-muted/40 border border-border flex items-center justify-between text-xs font-heading">
                    <span className="font-bold text-foreground-secondary">Calculated Line Total:</span>
                    <span className="font-mono font-black text-foreground text-sm">
                      ₹{(editingItem.quantity * editingItem.estimatedUnitPrice).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* Technical Specifications */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-foreground">Specifications / Notes</label>
                    <textarea
                      className="w-full px-3 py-2 text-xs font-medium rounded-xl border border-border bg-surface text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 min-h-[60px]"
                      placeholder="Enter model details, technical constraints, or packaging requirements..."
                      value={editingItem.specifications}
                      onChange={(e) => setEditingItem({ ...editingItem, specifications: e.target.value })}
                    />
                  </div>
                </div>

                <div className="p-4 border-t border-border bg-muted/20 flex items-center justify-end gap-2.5">
                  <Button variant="outline" size="sm" onClick={() => setIsEditorOpen(false)} className="rounded-xl font-bold cursor-pointer">
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveItem} className="rounded-xl font-bold cursor-pointer">
                    Save Line Item
                  </Button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
};
